import { MenuSidebarContext, generateItemsMenuTop } from './index.helpers'
import {
  StyledContainer,
  StyledExpandIcon,
  StyledNavigation,
  StyledNavigationMenu,
  StyledNavigationWrapper,
  StyledWrapper
} from './index.styled'
import { CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons'
import cn from 'classnames'
import { FC, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { IRoute } from 'routes/IRoute'

type MenuSidebarProps = {
  bgColor?: string
  stickyMenu?: boolean
  className?: string
  menus?: Array<IRoute>
}

const MenuExpandIcon = (props: { isOpen?: boolean }) => {
  const { collapse } = useContext(MenuSidebarContext)

  if (collapse) {
    return null
  }
  return (
    <StyledExpandIcon>
      {props.isOpen ? <CaretDownOutlined /> : <CaretRightOutlined />}
    </StyledExpandIcon>
  )
}
type MenuProps = {
  openKeys?: string[]
  active?: string[]
  menus?: Array<IRoute>
  onChange?: (e: any) => void
}
const MenuTop = ({ active, openKeys, menus, onChange }: MenuProps) => {
  let activeKey: any = active
  const { t } = useTranslation()
  const menusHasAccess = menus?.filter(
    (menu) => menu.hasAccess && !menu.hideOnSidebar
  )

  const items = generateItemsMenuTop(t, menusHasAccess || [])
  items.unshift({
    key: '/',
    icon: <img src="/images/ic-logo.svg" alt="" />,
    label: t('homepage')
  })

  const findActiveKey = items?.find((item) => item?.key === activeKey[0])
  if (!findActiveKey) {
    activeKey = [
      items?.find(
        (item, index) =>
          activeKey?.[0]?.includes(item?.key?.toString()) && index !== 0
      )?.key
    ]
  }

  return (
    <StyledNavigationMenu
      multiple
      mode="inline"
      items={items}
      openKeys={openKeys}
      selectedKeys={activeKey}
      rootClassName="menu--top"
      onClick={onChange}
      expandIcon={MenuExpandIcon}
    />
  )
}

const MenuSidebar: FC<MenuSidebarProps> = ({
  children,
  stickyMenu,
  className,
  menus
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { collapse, setCollapse, openKeys, setOpenKeys } =
    useContext(MenuSidebarContext)

  const active = useMemo(() => {
    let pathnameArr = location.pathname.split('/')
    if (pathnameArr?.length >= 4) {
      pathnameArr = pathnameArr.slice(0, 3)
    }
    return [pathnameArr.join('/')]
  }, [location.pathname])

  useEffect(() => {
    document.body.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [location.pathname])

  useEffect(() => {
    const pathnameArr = location.pathname.split('/')
    const openKs = [pathnameArr.slice(0, 2).join('/')]
    setOpenKeys(pathnameArr.length >= 4 || collapse ? undefined : openKs)
  }, [location.pathname, setOpenKeys, setCollapse])

  useEffect(() => {
    setOpenKeys((prevOpenKeys) => {
      if (collapse) {
        return undefined
      }
      return prevOpenKeys
    })
  }, [collapse, setOpenKeys])

  return (
    <StyledContainer hasSider className={cn(className, { stickyMenu })}>
      <StyledNavigation width={65}>
        <StyledNavigationWrapper>
          <MenuTop
            active={active}
            openKeys={openKeys}
            menus={menus}
            onChange={(e) => navigate(e.key)}
          />
        </StyledNavigationWrapper>
      </StyledNavigation>
      <StyledWrapper>{children}</StyledWrapper>
    </StyledContainer>
  )
}

export const MenuSidebarProvider: FC = ({ children }) => {
  const [collapse, setCollapse] = useState(false)
  const [openKeys, setOpenKeys] = useState<string[] | undefined>([])

  useEffect(() => {
    if (collapse) {
      document.body.classList.add('layout-sider-hidden')
    } else {
      document.body.classList.remove('layout-sider-hidden')
    }
  }, [collapse])

  return (
    <MenuSidebarContext.Provider
      value={{ collapse, setCollapse, openKeys, setOpenKeys }}>
      {children}
    </MenuSidebarContext.Provider>
  )
}

export default MenuSidebar
